import React, { useState } from 'react';
import cmsAPI from '../../services/api';

const AutoRAGChat: React.FC = () => {
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversation, setConversation] = useState<Array<{
    id: string;
    question: string;
    answer: string;
    sources?: Array<{ file: string; page?: number; url?: string }>;
    timestamp: string;
    provider: string;
    cached?: boolean;
    processing_time?: number;
  }>>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim() || loading) return;

    const currentQuestion = question.trim();
    setQuestion('');
    setLoading(true);

    try {
      const response = await cmsAPI.chatWithAutoRAG(currentQuestion);
      
      if (response.ok && response.data) {
        const newEntry = {
          id: Date.now().toString(),
          question: currentQuestion,
          answer: response.data.answer,
          sources: response.data.sources,
          timestamp: new Date().toISOString(),
          provider: response.data.provider,
          cached: response.data.cached,
          processing_time: response.data.processing_time,
        };
        
        setConversation(prev => [newEntry, ...prev]);
      } else {
        // Handle error
        const errorEntry = {
          id: Date.now().toString(),
          question: currentQuestion,
          answer: `Error: ${response.error?.message || 'Failed to get response'}`,
          timestamp: new Date().toISOString(),
          provider: 'error',
        };
        setConversation(prev => [errorEntry, ...prev]);
      }
    } catch (error) {
      console.error('Error in AutoRAG chat:', error);
      const errorEntry = {
        id: Date.now().toString(),
        question: currentQuestion,
        answer: 'Error: Failed to connect to AutoRAG service',
        timestamp: new Date().toISOString(),
        provider: 'error',
      };
      setConversation(prev => [errorEntry, ...prev]);
    } finally {
      setLoading(false);
    }
  };

  const getProviderBadge = (provider: string, cached?: boolean) => {
    let bgColor = 'bg-gray-100';
    let textColor = 'text-gray-800';
    let text = provider;

    if (provider === 'autorag') {
      bgColor = 'bg-primary-100';
      textColor = 'text-primary-800';
      text = cached ? 'AutoRAG (Cached)' : 'AutoRAG';
    } else if (provider === 'openai-fallback') {
      bgColor = 'bg-blue-100';
      textColor = 'text-blue-800';
      text = 'OpenAI Fallback';
    } else if (provider === 'error') {
      bgColor = 'bg-red-100';
      textColor = 'text-red-800';
      text = 'Error';
    }

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
        {text}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">AutoRAG Chat</h1>
        <p className="mt-1 text-sm text-gray-500">
          Test the AutoRAG integration with motorcycle manual questions
        </p>
      </div>

      {/* Chat Form */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="question" className="block text-sm font-medium text-gray-700 mb-2">
              Ask a question about motorcycle maintenance
            </label>
            <textarea
              id="question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="How do I change the oil in my Indian Chief?"
              rows={3}
              className="input-field resize-none"
              maxLength={75}
            />
            <div className="mt-1 text-xs text-gray-500">
              {question.length}/75 characters
            </div>
          </div>
          
          <button
            type="submit"
            disabled={loading || !question.trim() || question.length > 75}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Processing...
              </>
            ) : (
              'Ask AutoRAG'
            )}
          </button>
        </form>
      </div>

      {/* Conversation History */}
      {conversation.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Chat History</h2>
          
          {conversation.map((entry) => (
            <div key={entry.id} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              {/* Question */}
              <div className="mb-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-gray-600">Q</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-900 font-medium">{entry.question}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(entry.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Answer */}
              <div className="ml-11">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      {getProviderBadge(entry.provider, entry.cached)}
                      {entry.processing_time && (
                        <span className="text-xs text-gray-500">
                          {entry.processing_time}ms
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="prose prose-sm max-w-none">
                    <p className="text-gray-700">{entry.answer}</p>
                  </div>
                  
                  {/* Sources */}
                  {entry.sources && entry.sources.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs font-medium text-gray-500 mb-2">Sources:</p>
                      <div className="flex flex-wrap gap-2">
                        {entry.sources.map((source, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-blue-100 text-blue-800"
                          >
                            {source.file}
                            {source.page && ` (Page ${source.page})`}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {conversation.length === 0 && (
        <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 text-center">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl text-primary-600">🤖</span>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Start a conversation with AutoRAG
          </h3>
          <p className="text-gray-500">
            Ask questions about motorcycle maintenance and get AI-powered answers from your manual database.
          </p>
        </div>
      )}
    </div>
  );
};

export default AutoRAGChat;
